import { onBeforeUnmount, onMounted, ref } from 'vue'
import * as Sentry from '@sentry/vue'

// Safari before 16.4 only implements the prefixed Fullscreen API
type WebkitDocument = Document & {
  webkitFullscreenEnabled?: boolean
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}

type WebkitElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}

/**
 * False on iPhone Safari and inside iframes whose embedding page does not grant fullscreen
 * (`allow="fullscreen"` on the iframe).
 */
export function isFullscreenSupported(): boolean {
  const doc = document as WebkitDocument
  return Boolean(doc.fullscreenEnabled || doc.webkitFullscreenEnabled)
}

function getFullscreenElement(): Element | null {
  const doc = document as WebkitDocument
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

export async function toggleFullscreen(): Promise<void> {
  const doc = document as WebkitDocument
  if (getFullscreenElement()) {
    await (doc.exitFullscreen ? doc.exitFullscreen() : doc.webkitExitFullscreen?.())
    return
  }
  // The whole document goes fullscreen so dialogs, toasts and teleported content stay visible
  const root = document.documentElement as WebkitElement
  await (root.requestFullscreen ? root.requestFullscreen() : root.webkitRequestFullscreen?.())
}

export function useFullscreen() {
  const isSupported = ref(isFullscreenSupported())
  const isFullscreen = ref(Boolean(getFullscreenElement()))

  // Also fires when the user leaves fullscreen with Esc
  const syncFullscreen = () => {
    isFullscreen.value = Boolean(getFullscreenElement())
  }

  const toggle = async () => {
    try {
      await toggleFullscreen()
    } catch (error) {
      // The browser can still deny the request, e.g. without a user gesture
      if (import.meta.env.VITE_SENTRY_ENABLED === 'true') {
        Sentry.logger.warn('Fullscreen toggle failed', { error: String(error) })
      } else {
        console.warn('Fullscreen toggle failed', error)
      }
    }
    syncFullscreen()
  }

  onMounted(() => {
    document.addEventListener('fullscreenchange', syncFullscreen)
    document.addEventListener('webkitfullscreenchange', syncFullscreen)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('fullscreenchange', syncFullscreen)
    document.removeEventListener('webkitfullscreenchange', syncFullscreen)
  })

  return { isSupported, isFullscreen, toggle }
}
